import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  Timestamp,
  DocumentData
} from 'firebase/firestore'
import { db } from './firebase'

export interface UserRating {
  id: string
  userName: string
  ratings: Record<string, number>
  timestamp: number
  totalRatings: {
    notInterested: number
    interested: number
    mustSee: number
  }
  createdAt: Date
  parkComments?: Record<string, string> // parkName -> comment
}

// Firebase collection name
const RATINGS_COLLECTION = 'userRatings'

// Save user ratings to Firebase
export const saveUserRatings = async (rating: UserRating): Promise<void> => {
  try {
    console.log('Saving rating to Firebase:', rating)

    // Convert Date to Firestore Timestamp for storage
    const ratingData = {
      ...rating,
      createdAt: Timestamp.fromDate(rating.createdAt),
      timestamp: rating.timestamp
    }

    const docRef = await addDoc(collection(db, RATINGS_COLLECTION), ratingData)
    console.log('Rating saved successfully with ID:', docRef.id)
  } catch (error) {
    console.error('Error saving rating to Firebase:', error)
    throw new Error(`Failed to save rating: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

// Get all user ratings from Firebase
export const getAllUserRatings = async (): Promise<UserRating[]> => {
  try {
    console.log('Fetching all ratings from Firebase')

    const q = query(
      collection(db, RATINGS_COLLECTION),
      orderBy('timestamp', 'desc')
    )

    const querySnapshot = await getDocs(q)
    const ratings: UserRating[] = []

    querySnapshot.forEach((doc) => {
      const data = doc.data() as DocumentData
      ratings.push({
        id: doc.id,
        userName: data.userName,
        ratings: data.ratings,
        timestamp: data.timestamp,
        totalRatings: data.totalRatings,
        parkComments: data.parkComments,
        createdAt: data.createdAt?.toDate() || new Date(data.timestamp)
      })
    })

    console.log(`Fetched ${ratings.length} ratings from Firebase`)
    return ratings
  } catch (error) {
    console.error('Error fetching ratings from Firebase:', error)
    throw new Error(`Failed to fetch ratings: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

// Calculate attraction statistics from all ratings
export const getAttractionStats = async (): Promise<Record<string, { average: number; count: number; ratings: number[] }>> => {
  try {
    console.log('Calculating attraction stats from Firebase data')

    const allRatings = await getAllUserRatings()
    const stats: Record<string, { average: number; count: number; ratings: number[] }> = {}

    // Aggregate ratings by attraction
    allRatings.forEach(userRating => {
      Object.entries(userRating.ratings).forEach(([attraction, rating]) => {
        if (!stats[attraction]) {
          stats[attraction] = { average: 0, count: 0, ratings: [] }
        }
        stats[attraction].ratings.push(rating)
        stats[attraction].count++
      })
    })

    // Calculate averages
    Object.keys(stats).forEach(attraction => {
      const ratings = stats[attraction].ratings
      stats[attraction].average = ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length
    })

    console.log(`Calculated stats for ${Object.keys(stats).length} attractions`)
    return stats
  } catch (error) {
    console.error('Error calculating attraction stats:', error)
    throw new Error(`Failed to calculate stats: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

// Get comments grouped by park
export const getCommentsByPark = async (): Promise<Record<string, Array<{userName: string, comment: string, createdAt: Date}>>> => {
  try {
    console.log('Getting comments grouped by park')

    const allRatings = await getAllUserRatings()
    const commentsByPark: Record<string, Array<{userName: string, comment: string, createdAt: Date}>> = {}

    allRatings.forEach(rating => {
      console.log('Rating:', rating);
      if (rating.parkComments) {
        Object.entries(rating.parkComments).forEach(([parkName, comment]) => {
          if (comment && comment.trim()) {
            if (!commentsByPark[parkName]) {
              commentsByPark[parkName] = []
            }
            commentsByPark[parkName].push({
              userName: rating.userName,
              comment: comment.trim(),
              createdAt: rating.createdAt
            })
          }
        })
      }
    })

    // Sort comments by date (newest first)
    Object.keys(commentsByPark).forEach(parkName => {
      commentsByPark[parkName].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
    })

    console.log(`Found comments for ${Object.keys(commentsByPark).length} parks`)
    return commentsByPark
  } catch (error) {
    console.error('Error getting comments by park:', error)
    throw new Error(`Failed to get comments: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}
